import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { ProductKeys } from '@config/keys/product.keys';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { DoodProductModel } from '@core/models/product.model';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { PriceAtomComponent } from '@shared/atoms/price-atom/price-atom.component';
import { TagAtomComponent } from '@shared/atoms/tag-atom/tag-atom.component';
import { RadialGradientDirective } from '@shared/directives/radial-gradient.directive';
import { fromContentBuilderColor } from '@shared/utils/content-builder-color/content-builder-color';

@Component({
  selector: 'app-kiosk-product-card-v6-atom',
  templateUrl: './kiosk-product-card-v6-atom.component.html',
  styleUrls: ['./kiosk-product-card-v6-atom.component.scss'],
  imports: [
    CommonModule,
    ContentBuilderColorPipe,
    TranslateModule,
    PriceAtomComponent,
    TagAtomComponent,
    RadialGradientDirective,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      @apply p-2;
    }
    :host.borderedCard {
      @apply rounded-xl border border-solid border-neutral-300 bg-neutral-50 p-1;
      > div {
        @apply p-1 rounded-l-xl;
        .imageContainer {
          @apply rounded-l-xl;
        }
      }
    }

    :host.shadowCard {
      box-shadow: var(--kiosk-card-shadow);
      @apply rounded;
      .imageContainer {
        @apply rounded;
      }
    }

    :host.roundedCard {
      @apply rounded;
      .imageContainer {
        @apply rounded;
      }
    }
  `,
  host: {
    '[style.background-color]': 'this.cardColor() || "#ffffff"',
  },
})
export class KioskProductCardV6AtomComponent {
  readonly product = input.required<DoodProductModel>();
  readonly quantity = input.required<number | undefined>();
  readonly isProductAvailable = input<boolean>(true);
  readonly textColor = input<IContentBuilderFieldColor>();
  readonly cardColor = input(undefined, {
    transform: (value: undefined | IContentBuilderFieldColor) =>
      fromContentBuilderColor(value, '#ffffff'),
  });
  readonly buttonBackgroundColor = input<IContentBuilderFieldColor>();
  readonly unavailableButtonBackground = input<IContentBuilderFieldColor>();
  readonly unavailableButtonText = input<IContentBuilderFieldColor>();
  readonly buttonTextColor = input<IContentBuilderFieldColor>();

  readonly addProduct = output<void>();
  readonly showProduct = output<void>();

  public get ProductKeys(): typeof ProductKeys {
    return ProductKeys;
  }

  displayDiscount = computed(() => {
    const product = this.product();
    const originalPrice = product[ProductKeys.Price];
    const discount = product[ProductKeys.Discount] ?? 0;
    return originalPrice > 0 && discount > 0 && originalPrice > discount;
  });

  discountPercentage = computed(() => {
    const product = this.product();
    const originalPrice = product[ProductKeys.Price];
    const discount = product[ProductKeys.Discount] ?? 0;

    if (originalPrice <= 0 || discount <= 0 || originalPrice <= discount) {
      return 0;
    }

    const discountAmount = originalPrice - discount;
    const discountPercentage = Math.round((discountAmount / originalPrice) * 100);
    return discountPercentage;
  });

  addToCart(): void {
    this.addProduct.emit();
  }

  showProductModal(): void {
    this.showProduct.emit();
  }
}
